// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable } from 'inversify';

import Asset, { AssetDTO } from '@shared/models/asset';
import BaseService from '@core/services/base';
import { ListRequestParams } from '@shared/types/services';
import { AssetType } from '@shared/constants/asset';
import { TOWER_FIELDS } from '@shared/constants/asset';
import { REQUEST_PARAMS, ActivityFilter } from '@shared/constants/services';
import { AddPanelDTO, UpdateLatticeTowerMeasurementDTO, UpdateMemberDTO, UpdatePanelDTO } from '@shared/models/measurement-models';
import { UpdateEquipmentDTO } from '@shared/models/windarea';

@injectable()
export default class AssetsService extends BaseService<AssetDTO, Asset> {
  static diToken = Symbol('assets-service');
  constructor() {
    super({ domainArea: 'assets', Model: Asset });
  }

  // Get list of assets with pagination, sorting, and filtering.
  // Note: Removed ${TOWER_FIELDS.createdBy} from query because it may not be resolvable if user account has been deleted
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        assets(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { type: ${sorting?.type}, field: ${sorting?.field} }, 
          customerId: ${filtering?.customerId || null},
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all},
          ${REQUEST_PARAMS.filtering.fields.isBlocked}: ${filtering?.isBlocked ?? null}
        ) {
          items {
            ${TOWER_FIELDS.id},
            ${TOWER_FIELDS.name},
            ${TOWER_FIELDS.serialNumber},
            ${TOWER_FIELDS.customerName},
            ${TOWER_FIELDS.customerId},
            ${TOWER_FIELDS.type},
            ${TOWER_FIELDS.lastUpdated},
            ${TOWER_FIELDS.type},
            ${TOWER_FIELDS.createdById},
            ${TOWER_FIELDS.isBlocked},
            ${TOWER_FIELDS.isActive}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  private get assetPath() {
    return {
      [AssetType.tower]: 'tower',
      [AssetType.building]: 'building',
      [AssetType.mapping]: 'mapping',
    };
  }

  // Create an asset (?).
  async create(data: Partial<AssetDTO>, assetType: AssetType): Promise<{ assetId: AssetDTO['id'] }> {
    const url = this.getURL(this.assetPath[assetType]);
    const response = await Axios.post(url, data);

    return response.data;
  }

  // Read an asset with given Id and type.
  async get(id: Asset['id'], assetType: AssetType): Promise<Asset> {
    try {
      const url = this.getURL(`${this.assetPath[assetType]}/${id}`);
      const { data } = await Axios.get<Asset>(url);

      return new this.Model({ ...data, type: assetType });
    } catch (err) {
      throw err;
    }
  }

  // Update an asset with a given Id.
  edit(id: Asset['id'], data: Asset, assetType: AssetType): Promise<void> {
    const url = this.getURL(`${this.assetPath[assetType]}/${id}`);

    return Axios.put(url, data);
  }

  // Get info (position, type, ...) for a list of assets in an order.
  async getAssetsFullInfo(assetIds: number[]) {
    //console.log('assetIds ', assetIds)
    const url = 'assets/list';
    try {
      const { data } = await Axios.post(url, assetIds);

      //console.log(`**** get asset for ${assetIds}`, data);
      return data;
    } catch (err) {
      console.log("Exception in getAssetsFullInfo:", err);
      throw err;
    }
  }

  // Get Lattice measurement info (connection, leg/bracing type, ...) for a list of lattice assets in an order.
  async getLatticeAssetsTowerMeasurement(assetIds: number[]) {
    //console.log('assetIds ', assetIds)
    const url = 'assets/latticetowermeasurementlist';
    try {
      const { data } = await Axios.post(url, assetIds);

      //console.log(`**** get lattice asset tower measurement for ${assetIds}`, data);
      return data;
    } catch (err) {
      console.log("Exception in getLatticeAssetsTowerMeasurement:", err);
      throw err;
    }
  }

  // Get Equipment info for a list of assets in an order.
  async getTowerEquipments(assetIds: number[]) {
    //console.log('assetIds ', assetIds)
    const url = 'assets/towerequipmentlist';
    try {
      const { data } = await Axios.post(url, assetIds);

      //console.log(`**** get lattice asset tower measurement for ${assetIds}`, data);
      return data;
    } catch (err) {
      console.log("Exception in getEquipments:", err);
      throw err;
    }
  }

  // Update Lattice measurement info (connection, leg/bracing type, ...) for a list of lattice assets in an order.
  async updateLatticeTowerMeasurement(params: Array<UpdateLatticeTowerMeasurementDTO>): Promise<Array<number>> {
    //console.log('updateLatticeTowerMeasurement:', params)
    const url = 'assets/updatelatticetowermeasurement';
    try {
      const { data } = await Axios.put(url, params);

      //console.log(`**** update lattice tower measurement (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in updateLatticeTowerMeasurement:", err);
      throw err;
    }
  }

  // Update Lattice measurement panels.
  async updateLatticeTowerPanel(params: Array<UpdatePanelDTO>): Promise<Array<number>> {
    //console.log('updateLatticeTowerPanel:', params)
    const url = 'assets/updatelatticetowerpanel';
    try {
      const { data } = await Axios.put(url, params);

      //console.log(`**** update lattice tower panel (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in updateLatticeTowerPanel:", err);
      throw err;
    }
  }

  // Update Lattice measurement panel members.
  async updateLatticeTowerPanelMembers(params: Array<UpdateMemberDTO>): Promise<Array<number>> {
    //console.log('updateLatticeTowerPanelMembers:', params)
    const url = 'assets/updatelatticetowerpanelmember';
    try {
      const { data } = await Axios.put(url, params);

      //console.log(`**** update lattice tower panel (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in updateLatticeTowerPanelMembers:", err);
      throw err;
    }
  }

  // Add a Lattice measurement panel.
  async addLatticeTowerPanel(param: AddPanelDTO): Promise<number> {
    //console.log('addLatticeTowerPanel:', param)
    const url = 'assets/addlatticetowerpanel';
    try {
      const { data } = await Axios.post(url, param);

      //console.log(`**** add lattice tower panel (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in addLatticeTowerPanel:", err);
      throw err;
    }
  }

  // Delete a list Lattice measurement panels.
  async deleteLatticeTowerPanels(param: Array<number>): Promise<Array<number>> {
    //console.log('deleteLatticeTowerPanel:', param)
    const url = 'assets/deletelatticetowerpanel';
    try {
      const { data } = await Axios.put(url, param);

      //console.log(`**** delete lattice tower panels (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in addLatticeTowerPanel:", err);
      throw err;
    }
  }

  // Add a Lattice measurement panel member.
  async addLatticeTowerPanelMember(param: UpdateMemberDTO): Promise<number> {
    //console.log('addLatticeTowerPanelMember:', param)
    const url = 'assets/addlatticetowerpanelmember';
    try {
      const { data } = await Axios.post(url, param);

      //console.log(`**** add lattice tower panel member (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in addLatticeTowerPanelMember:", err);
      throw err;
    }
  }

  // Delete a list Lattice measurement panel members.
  async deleteLatticeTowerPanelMembers(param: Array<number>): Promise<Array<number>> {
    //console.log('deleteLatticeTowerPanel:', param)
    const url = 'assets/deletelatticetowerpanelmember';
    try {
      const { data } = await Axios.put(url, param);

      //console.log(`**** delete lattice tower panel members (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in deleteLatticeTowerPanelMembers:", err);
      throw err;
    }
  }

  // Get Lattice panel info (height, type, members ...) for a list of lattice assets in an order.
  async getLatticeAssetsPanels(assetIds: number[]) {
    //console.log('assetIds ', assetIds)
    const url = 'assets/latticetowerpanellist';
    try {
      const { data } = await Axios.post(url, assetIds);

      //console.log(`**** get lattice asset panels for ${assetIds}`, data);
      return data;
    } catch (err) {
      console.log("Exception in getLatticeAssetsPanels:", err);
      throw err;
    }
  }

  // Add a tower equipment.
  async addTowerEquipment(param: UpdateEquipmentDTO): Promise<number> {
    //console.log('addTowerEquipment:', param)
    const url = 'assets/addtowerequipment';
    try {
      const { data } = await Axios.post(url, param);

      //console.log(`**** add a new tower equipment (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in addTowerEquipment:", err);
      throw err;
    }
  }

  // Delete a list equipments.
  async deleteTowerEquipments(param: Array<number>): Promise<Array<number>> {
    //console.log('deleteTowerEquipments:', param)
    const url = 'assets/deletetowerequipments';
    try {
      const { data } = await Axios.put(url, param);

      //console.log(`**** delete tower equipments (${params})`, param);
      return data;
    } catch (err) {
      console.log("Exception in deleteTowerEquipments:", err);
      throw err;
    }
  }

  // Update tower equipments.
  async updateTowerEquipments(params: Array<UpdateEquipmentDTO>): Promise<Array<number>> {
    //console.log('updateLatticeTowerPanelMembers:', params)
    const url = 'assets/updatetowerequipments';
    try {
      const { data } = await Axios.put(url, params);

      //console.log(`**** update lattice tower panel (${params})`, data);
      return data;
    } catch (err) {
      console.log("Exception in updatetowerequipments:", err);
      throw err;
    }
  }

  // Get list of site types from portal-api.
  async getSiteTypes(): Promise<Array<{id: number, name: string}>> {
    const url = 'assets/site-types';
    try {
      const { data } = await Axios.get(url);

      return data;
    } 
    catch (err) {
      console.log("Exception in getSiteTypes:", err);
      throw err;
    }
  }
}
